import cx from 'classnames';
import * as _ from 'lodash';
import { useRef, useState } from 'react';
import type { MouseEvent, ReactElement, VoidFunctionComponent } from 'react';

import { useToggle } from '../../../hooks';
import Button, { BUTTON_TYPE } from '../Button';

import VisibilityWrapper from './VisibilityWrapper';

import styles from './DynamicShowMoreList.module.less';

type ShowMoreButtonProps = {
  isShowing: boolean;
  toggleIsShowing: () => void;
};

const ShowMoreButton: VoidFunctionComponent<ShowMoreButtonProps> = ({
  isShowing,
  toggleIsShowing,
}) => {
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    toggleIsShowing();
  };

  return (
    <span className={styles.showMoreButton}>
      (
      <Button type={BUTTON_TYPE.ANCHOR} onClick={handleClick}>
        {isShowing ? 'Show less' : 'Show more'}
      </Button>
      )
    </span>
  );
};

type DynamicShowMoreListProps = {
  // List array to display
  list: Array<string | ReactElement>;
  // Line height a list item (eg, 27 for badges)
  // this is necessary to clip the list to one line, reliably. The container must be of the same height as the list items.
  lineHeight: number;
  // Align the flex items to the right.
  isRightAligned?: boolean;
  // Use larger spacing between items.
  hasLargeGap?: boolean;
};

/**
 * A component that displays a list of items, truncating overflow with a "show more"/"show less" button.
 *
 * In order to display cleanly, while collapsed, the list items should expand to a width of 100%. All items must also be a consistent height.
 * In order
 * @example
 * const list = ['Item 1', 'Item 2', 'Item 3'];
 * return (
 *   <DynamicShowMoreList list={list} lineHeight={27} />
 * );
 */
const DynamicShowMoreList: VoidFunctionComponent<DynamicShowMoreListProps> = ({
  list,
  lineHeight,
  isRightAligned,
  hasLargeGap,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isShowing, toggleIsShowing] = useToggle(false);
  const [isVisibleList, setIsVisibleList] = useState<boolean[]>([]);

  // Call back for when element is visible on screen
  const onElementVisible = (index: number) => (isVisible: boolean) => {
    setIsVisibleList((prevState) => {
      // Don't update state if it's the same as before
      if (prevState[index] === isVisible) {
        return prevState;
      }

      const newState = [...prevState];
      newState[index] = isVisible;
      return newState;
    });
  };

  const itemList = list.map((listItem, index) => (
    <VisibilityWrapper
      key={_.isString(listItem) ? listItem : listItem?.props.key}
      className={cx(styles.ListItem, { [styles.ListItemAlignRight]: isRightAligned })}
      onElementVisible={onElementVisible(index)}
    >
      {listItem}
    </VisibilityWrapper>
  ));

  const isOverflowing = isVisibleList.some((isVisible) => !isVisible);

  return (
    <div
      className={cx(styles.showMore, {
        [styles.showAll]: isShowing,
        [styles.showMoreAlignRight]: isRightAligned,
        [styles.showMoreLargeGap]: hasLargeGap,
      })}
      style={{ maxHeight: `${lineHeight}px` }}
    >
      <div
        className={cx(styles.showMoreList, {
          [styles.showMoreListAlignRight]: isRightAligned,
          [styles.showMoreListLargeGap]: hasLargeGap,
        })}
        ref={ref}
      >
        {itemList}
        {isShowing && (
          <ShowMoreButton
            isShowing={isShowing}
            toggleIsShowing={toggleIsShowing}
          />
        )}
      </div>
      {isOverflowing && !isShowing && (
        <ShowMoreButton
          isShowing={isShowing}
          toggleIsShowing={toggleIsShowing}
        />
      )}
    </div>
  );
};

export default DynamicShowMoreList;
